import { useContext, useState } from 'react'
import { useParams } from 'react-router-dom'

import Divider from '../Divider'
import InputField from '../InputField'
import WaveLoading from 'components/WaveLoading'
import OrganizationsContext from 'containers/organizations/organizationsContext'
import api from 'containers/api'
import { useSelector } from 'react-redux'
import { selectToken } from 'containers/authentication/authenticationSlice'

const Guide = ({ onHide, show }) => {
  return (
    <div className={`guide ${show ? 'show' : ''}`}>
      <button onClick={() => onHide()} className="close-button">
        &times;
      </button>
      <h1>App Store Connect API Setup Guide</h1>
      <Divider spaceY={10} />

      <div className="step-heading">
        <p>STEP 1</p>
        <h2>Create API Key in App Store Connect</h2>
      </div>
      <ol>
        <li>Log in to <a href="https://appstoreconnect.apple.com" target="_blank" rel="noopener noreferrer">App Store Connect</a></li>
        <li>Navigate to <b>Users and Access → Integrations → App Store Connect API</b></li>
        <li>Under <b>Team Keys</b>, click the <b>+</b> button to generate a new API key</li>
        <li>Enter a name (e.g., "Maya Insights Integration")</li>
        <li>Select <b>App Manager</b> role (required for analytics and reviews access)</li>
        <li>Click <b>Generate</b></li>
      </ol>

      <div className="step-heading">
        <p>STEP 2</p>
        <h2>Download Private Key</h2>
      </div>
      <ol>
        <li>After generation, click <b>Download API Key</b></li>
        <li>Save the <code>.p8</code> file securely</li>
        <li><b>Important:</b> You can only download the key once. Keep it safe!</li>
        <li>Note the <b>Key ID</b> (shown above the download button)</li>
        <li>Note the <b>Issuer ID</b> (shown at the top of the API Keys page)</li>
      </ol>

      <div className="step-heading">
        <p>STEP 3</p>
        <h2>Connect to Maya Insights</h2>
      </div>
      <ol>
        <li>Open the downloaded <code>.p8</code> file in a text editor</li>
        <li>Copy the entire content (including BEGIN and END lines)</li>
        <li>Paste below along with your Key ID and Issuer ID</li>
        <li>Click <b>Connect</b></li>
      </ol>
    </div>
  )
}

const AppleAppStore = () => {
  const { uuid: serviceUUID } = useParams<{ uuid: string }>()
  const { currentOrganization } = useContext(OrganizationsContext)
  const token = useSelector(selectToken)

  const [showGuide, setShowGuide] = useState(false)
  const [keyId, setKeyId] = useState<string>('')
  const [issuerId, setIssuerId] = useState<string>('')
  const [privateKey, setPrivateKey] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleKeyIdChange = (value: string) => {
    setKeyId(value)
    if (error) setError(null)
  }

  const handleIssuerIdChange = (value: string) => {
    setIssuerId(value)
    if (error) setError(null)
  }

  const handlePrivateKeyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPrivateKey(e.target.value)
    if (error) setError(null)
  }

  const handleConnect = async () => {
    if (!keyId || !issuerId || !privateKey) {
      setError('All fields are required')
      return
    }

    if (!currentOrganization || !currentOrganization.slug) {
      setError('Organization not loaded. Please refresh the page.')
      return
    }

    setError(null)
    setLoading(true)

    try {
      // Call generic API credentials connect endpoint
      const response = await api.post(
        `/services/${serviceUUID}/credentials/connect`,
        {
          credentials: {
            key_id: keyId.trim(),
            issuer_id: issuerId.trim(),
            private_key: privateKey.trim()
          }
        },
        api.getHeaders(token),
        { organization_id: currentOrganization.id }
      )

      const data = response.data || response

      // Check for successful connection (credentials_id indicates success)
      if (data.credentials_id) {
        setSuccess(true)
        setError(null)
        // Redirect to sources page after 2 seconds
        setTimeout(() => {
          window.location.href = `/organization/${currentOrganization.slug}/sources`
        }, 2000)
      } else {
        setError(data.message || 'Failed to connect. Please check your credentials and try again.')
        setLoading(false)
      }
    } catch (err: any) {
      console.error('App Store Connect error:', err)
      const errorMessage =
        err.response?.data?.detail?.message ||
        err.response?.data?.message ||
        'Failed to connect. Please verify your App Store Connect API credentials.'
      setError(errorMessage)
      setLoading(false)
    }
  }

  return (
    <div className="guide-container">
      <Guide onHide={() => setShowGuide(false)} show={showGuide} />

      <div style={{ paddingLeft: '20px', paddingBottom: '5rem' }}>
        <img
          src="https://developer.apple.com/assets/elements/icons/app-store-connect/app-store-connect-96x96_2x.png"
          width="96"
          alt="App Store Connect"
          onError={(e) => {
            e.currentTarget.style.display = 'none'
          }}
        />

        <h2 style={{ fontWeight: 'normal', marginTop: '1rem' }}>Connect App Store Connect API</h2>
        <p style={{ color: '#9b9b9b', fontSize: '14px' }}>
          Access app analytics, reviews, and performance data. Press the{' '}
        <button
          style={{ verticalAlign: 'initial' }}
          className="button-tip"
          onClick={() => setShowGuide(true)}
        >
          Guide
        </button>{' '}
        button for detailed instructions.
      </p>

      <Divider spaceY={20} />

      {success ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem',
            width: '100%',
            maxWidth: '600px',
            padding: '2rem',
            backgroundColor: '#f0f9ff',
            border: '2px solid #22c55e',
            borderRadius: '8px'
          }}
        >
          <div style={{ fontSize: '48px', color: '#22c55e' }}>✓</div>
          <h3 style={{ color: '#22c55e', margin: 0 }}>Successfully Connected!</h3>
          <p style={{ color: '#666', textAlign: 'center', margin: 0 }}>
            Your App Store Connect account is now connected. Redirecting...
          </p>
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '1rem',
            width: '100%',
            maxWidth: '600px'
          }}
        >
          <h3 style={{ fontWeight: 'normal', marginBottom: 0 }}>Key ID</h3>
          <p style={{ color: '#9b9b9b', fontSize: '14px', margin: '0' }}>
            The Key ID from your App Store Connect API key (e.g., <code>ABC123DEFG</code>)
          </p>

          <InputField
            type="text"
            label=""
            placeholder="ABC123DEFG"
            value={keyId}
            setValue={handleKeyIdChange}
            style={{
              border: error ? '1px solid #ff4444' : undefined,
              width: '100%'
            }}
            disabled={loading}
          />

          <h3 style={{ fontWeight: 'normal', marginBottom: 0, marginTop: '1rem' }}>Issuer ID</h3>
          <p style={{ color: '#9b9b9b', fontSize: '14px', margin: '0' }}>
            The Issuer ID from your App Store Connect team (UUID format)
          </p>

          <InputField
            type="text"
            label=""
            placeholder="12345678-1234-1234-1234-123456789012"
            value={issuerId}
            setValue={handleIssuerIdChange}
            style={{
              border: error ? '1px solid #ff4444' : undefined,
              width: '100%'
            }}
            disabled={loading}
          />

          <h3 style={{ fontWeight: 'normal', marginBottom: 0, marginTop: '1rem' }}>
            Private Key (.p8 file content)
          </h3>
          <p style={{ color: '#9b9b9b', fontSize: '14px', margin: '0' }}>
            Paste the entire contents of your downloaded .p8 file, including the BEGIN and END lines
          </p>

          <textarea
            placeholder="-----BEGIN PRIVATE KEY-----
MIGTAgEAMBMGByqGSM49AgEGCCqGSM49AwEHBHkwdwIBAQQg...
-----END PRIVATE KEY-----"
            value={privateKey}
            onChange={handlePrivateKeyChange}
            rows={10}
            style={{
              border: error ? '1px solid #ff4444' : '1px solid #ddd',
              borderRadius: '4px',
              padding: '0.75rem',
              fontFamily: 'monospace',
              fontSize: '13px',
              width: '100%',
              resize: 'vertical',
              backgroundColor: loading ? '#f5f5f5' : 'white'
            }}
            disabled={loading}
          />

          <button
            className="submit-button"
            onClick={handleConnect}
            disabled={!keyId || !issuerId || !privateKey || loading}
            style={{
              margin: 0,
              width: '100%',
              opacity: !keyId || !issuerId || !privateKey || loading ? 0.5 : 1
            }}
          >
            {loading ? 'Connecting...' : 'Connect'}
          </button>

          {error && (
            <div
              style={{
                color: '#ff4444',
                fontSize: '14px',
                padding: '0.75rem',
                backgroundColor: '#fff5f5',
                border: '1px solid #ffcccc',
                borderRadius: '4px',
                width: '100%'
              }}
            >
              {error}
            </div>
          )}

          {loading && (
            <div style={{ width: '100%', textAlign: 'center', padding: '1rem' }}>
              <WaveLoading barColor={'green'} />
              <p style={{ color: '#9b9b9b', fontSize: '14px', marginTop: '1rem' }}>
                Validating credentials and connecting...
              </p>
            </div>
          )}

          <div
            style={{
              backgroundColor: '#f5f5f5',
              padding: '1rem',
              borderRadius: '4px',
              fontSize: '14px',
              color: '#666',
              width: '100%',
              marginTop: '1rem'
            }}
          >
            <strong>Note:</strong> Your API credentials are securely encrypted and stored. We only
            request read permissions to sync your app analytics and customer reviews.
          </div>
        </div>
      )}
      </div>

      <div className="guide-bar">
        <button className="submit-button guide-button" onClick={() => setShowGuide(true)}>
          <span>Guide</span>
          <span className="material-icons-outlined saved-icon non-selectable">menu_book</span>
        </button>
      </div>
    </div>
  )
}

export default AppleAppStore
